import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    SITE_URL,
    SITE_NAME,
    TWITTER_HANDLE,
    DUPLICATE_ROUTES,
    getRouteMeta,
} from './siteMeta';

/**
 * Per-route metadata for a client-rendered app.
 *
 * This is a single-page app, so without this every route shipped the same
 * title, description and share card. Rendered once inside the router, it
 * rewrites the document head whenever the location changes.
 *
 * Written against the DOM directly rather than pulling in react-helmet: the
 * whole job is a dozen tags, and the dependency would be larger than the code.
 *
 * Crawlers that execute JavaScript (Google, Bing) read these. Most social
 * scrapers do NOT run JS, so the tags in public/index.html remain the fallback
 * for shared links — see the note in index.html about prerendering if
 * per-route share cards become important.
 */

const setTag = (selector: string, create: () => HTMLElement, content: string) => {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) {
        el = create();
        document.head.appendChild(el);
    }
    if (el.tagName === 'LINK') el.setAttribute('href', content);
    else el.setAttribute('content', content);
};

const meta = (name: string, content: string) =>
    setTag(`meta[name="${name}"]`, () => {
        const el = document.createElement('meta');
        el.setAttribute('name', name);
        return el;
    }, content);

const prop = (property: string, content: string) =>
    setTag(`meta[property="${property}"]`, () => {
        const el = document.createElement('meta');
        el.setAttribute('property', property);
        return el;
    }, content);

const link = (rel: string, href: string) =>
    setTag(`link[rel="${rel}"]`, () => {
        const el = document.createElement('link');
        el.setAttribute('rel', rel);
        return el;
    }, href);

const Seo: React.FC = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const m = getRouteMeta(pathname);
        const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
        const url = `${SITE_URL}${clean === '/' ? '/' : clean}`;
        const image = `${SITE_URL}/og/${m.image}.png`;

        document.title = m.title;
        meta('description', m.description);

        // Alias routes render the same page; point them at the real one so they
        // do not compete with it in search.
        const isDuplicate = DUPLICATE_ROUTES.includes(clean);
        link('canonical', isDuplicate ? `${SITE_URL}/downloadapp` : url);
        meta(
            'robots',
            m.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'
        );

        prop('og:title', m.title);
        prop('og:description', m.description);
        prop('og:url', url);
        prop('og:image', image);
        prop('og:image:width', '1200');
        prop('og:image:height', '630');
        prop('og:image:alt', m.title);
        prop('og:type', clean.startsWith('/blog/') ? 'article' : 'website');
        prop('og:site_name', SITE_NAME);

        meta('twitter:card', 'summary_large_image');
        meta('twitter:site', TWITTER_HANDLE);
        meta('twitter:creator', TWITTER_HANDLE);
        meta('twitter:title', m.title);
        meta('twitter:description', m.description);
        meta('twitter:image', image);
        meta('twitter:image:alt', m.title);
    }, [pathname]);

    return null;
};

export default Seo;
